import {
  associateWebAuthnCredential,
  deleteWebAuthnCredential,
  listWebAuthnCredentials,
  signIn,
  signOut,
} from "aws-amplify/auth";

import { isDefined } from "@clipboard-health/util-ts";
import { Platform } from "@src/mobile/utils/platform";

import { type CognitoUserCredential } from "./cognitoAuth";
import { fetchAuthSessionWithLock } from "./cognitoSessionLock";
import { isNativePasskeyReady } from "./passkeyNativeBridge";

/**
 * Passkey (WebAuthn) authentication via Cognito's choice-based `USER_AUTH`
 * flow. PoC scope (DEVOP-5458): enough to register a passkey while signed in
 * and to sign in with it. Gated behind `ENABLE_ADMIN_APP_PASSKEY_AUTH`.
 *
 * Requires the user pool to have WebAuthn enabled and the app client to allow
 * `ALLOW_USER_AUTH`.
 */

/**
 * Whether the current runtime can run a passkey ceremony. Used to hide the
 * passkey UI where it can't work.
 *
 * On web this checks the standard WebAuthn APIs. In the Capacitor native build
 * those APIs are absent/non-functional in the WebView, so support is reported
 * by the native shim (see `passkeyNativeBridge.ts`) once it has installed and
 * confirmed device capability during app bootstrap.
 */
export function isPasskeySupported(): boolean {
  if (Platform.isMobile) {
    return isNativePasskeyReady();
  }

  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator?.credentials?.get === "function"
  );
}

export type PasskeySignInResult =
  | { status: "signed-in"; credential: CognitoUserCredential }
  | { status: "no-passkey" };

interface ConditionalPublicKeyCredentialConstructor {
  isConditionalMediationAvailable?: () => Promise<boolean>;
}

interface PasskeySignInOptions {
  mediation?: "conditional";
  signal?: AbortSignal;
}

let activeConditionalCredentialRequest: Promise<void> | undefined;

function isUserAlreadyAuthenticatedException(error: unknown): boolean {
  return (
    error !== null &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "UserAlreadyAuthenticatedException"
  );
}

export async function isConditionalPasskeySignInAvailable(): Promise<boolean> {
  if (Platform.isMobile || !isPasskeySupported()) {
    return false;
  }

  const publicKeyCredential =
    window.PublicKeyCredential as unknown as ConditionalPublicKeyCredentialConstructor;

  if (typeof publicKeyCredential.isConditionalMediationAvailable !== "function") {
    return false;
  }

  try {
    return await publicKeyCredential.isConditionalMediationAvailable();
  } catch {
    return false;
  }
}

async function initiatePasskeySignIn(email: string, options?: PasskeySignInOptions) {
  const runSignIn = () =>
    signIn({
      username: email,
      options: { authFlowType: "USER_AUTH", preferredChallenge: "WEB_AUTHN" },
    });

  if (options?.mediation !== "conditional") {
    while (isDefined(activeConditionalCredentialRequest)) {
      await activeConditionalCredentialRequest;
    }
    return runSignIn();
  }

  return withConditionalCredentialRequest({
    signal: options.signal,
    run: runSignIn,
  });
}

async function withConditionalCredentialRequest<Result>({
  signal,
  run,
}: {
  signal?: AbortSignal;
  run: () => Promise<Result>;
}): Promise<Result> {
  while (isDefined(activeConditionalCredentialRequest)) {
    await activeConditionalCredentialRequest;
  }

  let resolveActiveCredentialRequest: () => void = () => {};
  const currentCredentialRequest = new Promise<void>((resolve) => {
    resolveActiveCredentialRequest = resolve;
  });
  activeConditionalCredentialRequest = currentCredentialRequest;

  const credentials = navigator.credentials as CredentialsContainer & {
    get: CredentialsContainer["get"];
  };
  const originalGet = credentials.get;
  const boundGet = originalGet.bind(credentials);

  // Amplify owns the Cognito WEB_AUTHN challenge handling, but does not expose
  // the browser mediation option. Patch only the ceremony it starts here.
  credentials.get = ((requestOptions?: CredentialRequestOptions) => {
    return boundGet({
      ...requestOptions,
      mediation: "conditional",
      ...(signal ? { signal } : {}),
    });
  }) as CredentialsContainer["get"];

  try {
    return await run();
  } finally {
    credentials.get = originalGet;
    if (activeConditionalCredentialRequest === currentCredentialRequest) {
      activeConditionalCredentialRequest = undefined;
    }
    resolveActiveCredentialRequest();
  }
}

/**
 * Attempts a passkey sign-in for `email`.
 *
 * Passing `preferredChallenge: "WEB_AUTHN"` makes Amplify run the browser
 * `navigator.credentials.get()` ceremony synchronously inside this call when
 * the user has a passkey — so callers must invoke this directly from the click
 * handler (no prior `await`) to satisfy Safari's user-gesture requirement.
 *
 * Returns `"no-passkey"` when the email has no registered passkey (Cognito
 * responds with first-factor selection instead of running the ceremony).
 */
export async function signInWithPasskey(
  email: string,
  options?: PasskeySignInOptions
): Promise<PasskeySignInResult> {
  let result;
  try {
    result = await initiatePasskeySignIn(email, options);
  } catch (error: unknown) {
    if (!isUserAlreadyAuthenticatedException(error)) {
      throw error;
    }
    // A previous Cognito session still exists; clear it and retry.
    await signOut();
    result = await initiatePasskeySignIn(email, options);
  }

  if (result.isSignedIn) {
    const session = await fetchAuthSessionWithLock();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) {
      throw new Error("Missing Cognito id token after passkey authentication");
    }
    return {
      status: "signed-in",
      credential: { idToken, accessToken: session.tokens?.accessToken?.toString() },
    };
  }

  // WEB_AUTHN was unavailable for this user — Cognito offered factor selection
  // instead of running the ceremony. Treat as "no passkey" and abandon the
  // in-progress USER_AUTH session so it can't leak into later attempts. The
  // cleanup is best-effort: a failed signOut must not mask the "no passkey"
  // result with a generic error.
  try {
    await signOut();
  } catch {
    // Ignore — abandoning the partial session is best-effort.
  }
  return { status: "no-passkey" };
}

/**
 * Registers a new passkey for the currently authenticated user. Triggers the
 * browser `navigator.credentials.create()` ceremony; must be called from a
 * user gesture. The user must already be signed in (Cognito does not allow
 * passkey creation at signup).
 */
export async function registerPasskey(): Promise<void> {
  await associateWebAuthnCredential();
}

export interface Passkey {
  credentialId: string;
  friendlyName: string;
  createdAt?: Date;
}

/** Lists the WebAuthn credentials (passkeys) registered for the signed-in user. */
export async function listPasskeys(): Promise<Passkey[]> {
  const result = await listWebAuthnCredentials();
  return (result.credentials ?? [])
    .filter((credential): credential is typeof credential & { credentialId: string } =>
      Boolean(credential.credentialId)
    )
    .map((credential) => ({
      credentialId: credential.credentialId,
      friendlyName: credential.friendlyCredentialName ?? "",
      createdAt: credential.createdAt ? new Date(credential.createdAt) : undefined,
    }));
}

/** Removes a passkey by its credential ID. */
export async function deletePasskey(credentialId: string): Promise<void> {
  await deleteWebAuthnCredential({ credentialId });
}
